import { useState } from "react";
import { Archive, Download, Eye, EyeOff, Paperclip } from "lucide-react";
import { Button } from "../../components/Button";
import { attachmentsZipUrl, attachmentUrl, previewKind } from "../../api/mailbox";
import type { MailScope } from "../../api/mailbox";
import type { MailAttachment } from "../../api/types";
import { formatSize } from "./MailReader";

// 附件清單 + 預覽 + 全部下載(規格 §2.6「附件預覽 + 全部下載」)。
//
// - **只有後端肯 `?inline=1` 的型別**(PDF、png / jpeg / gif / webp)才有「預覽」鈕;SVG、HTML
//   與其他一律只能下載 —— 依附件清單上的 `content_type` 判斷,前端連試都不試。
// - PDF 放 `<iframe sandbox>`(**不給 allow-scripts**)。有的瀏覽器在 sandbox 裡不肯畫 PDF,
//   所以每個預覽旁邊都有「下載」連結當退路。圖片用 `<img>`。
// - 兩個以上附件才有「全部下載(zip)」。

export function AttachmentPreview({
  messageId,
  attachments,
  scope = "messages",
}: {
  messageId: number;
  attachments: MailAttachment[];
  /** 「系統寄出」的信走另一組端點。 */
  scope?: MailScope;
}) {
  const [open, setOpen] = useState<number | null>(null);
  if (attachments.length === 0) return null;
  return (
    <section aria-label="附件" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="m-0 text-sm font-medium text-[var(--text-2)]">附件</h4>
        {attachments.length > 1 ? (
          <a
            href={attachmentsZipUrl(messageId, scope)}
            download
            className="inline-flex items-center gap-1.5 text-sm text-[var(--accent-hover)] underline-offset-2 hover:underline print:hidden"
          >
            <Archive aria-hidden="true" size={14} />
            全部下載(zip)
          </a>
        ) : null}
      </div>
      <ul className="m-0 flex flex-col gap-1 p-0">
        {attachments.map((one) => {
          const name = one.filename || "(沒有檔名)";
          const kind = previewKind(one.content_type);
          const shown = open === one.part && kind !== null;
          return (
            <li key={one.part} className="flex list-none flex-col gap-1.5">
              <span className="flex flex-wrap items-center gap-2">
                <a
                  href={attachmentUrl(messageId, one.part, false, scope)}
                  download={one.filename || true}
                  className="inline-flex items-center gap-1.5 text-sm text-[var(--accent-hover)] underline-offset-2 hover:underline"
                >
                  <Paperclip aria-hidden="true" size={14} />
                  {name}
                  <span className="text-xs text-[var(--text-3)]">({formatSize(one.size)})</span>
                </a>
                {kind ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`${shown ? "收起預覽" : "預覽"} ${name}`}
                    aria-expanded={shown}
                    icon={shown ? <EyeOff aria-hidden="true" size={14} /> : <Eye aria-hidden="true" size={14} />}
                    onClick={() => setOpen(shown ? null : one.part)}
                    className="print:hidden"
                  >
                    {shown ? "收起" : "預覽"}
                  </Button>
                ) : null}
              </span>
              {shown ? (
                <div className="flex flex-col gap-1 print:hidden">
                  {kind === "pdf" ? (
                    <iframe
                      title={`預覽:${name}`}
                      src={attachmentUrl(messageId, one.part, true, scope)}
                      sandbox=""
                      referrerPolicy="no-referrer"
                      className="h-[70vh] min-h-64 w-full rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]"
                    />
                  ) : (
                    <img
                      src={attachmentUrl(messageId, one.part, true, scope)}
                      alt={name}
                      referrerPolicy="no-referrer"
                      className="max-h-[70vh] max-w-full self-start rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-2)] object-contain"
                    />
                  )}
                  <a
                    href={attachmentUrl(messageId, one.part, false, scope)}
                    download={one.filename || true}
                    aria-label={`下載 ${name}`}
                    className="inline-flex items-center gap-1.5 self-start text-xs text-[var(--text-2)] underline-offset-2 hover:underline"
                  >
                    <Download aria-hidden="true" size={12} />
                    看不到預覽?下載
                  </a>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
